import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { ToastContainer } from './components/common/Toast';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import SupportChatWidget from './components/SupportChatWidget';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import EventsPage from './pages/EventsPage';
import CityEventsPage from './pages/CityEventsPage';
import AuthPage from './pages/AuthPage';
import OnboardingPage from './pages/OnboardingPage';
import { useAuth } from './context/AuthContext';
import { initializeCsrf } from './utils/api';
import PasswordGate from './components/PasswordGate';
import { CITY_SLUGS } from './constants/cities';

// Code-split pages to keep the initial bundle small
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ProposalPage = lazy(() => import('./pages/ProposalPage'));
const MagazinePage = lazy(() => import('./pages/MagazinePage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const IssuePage = lazy(() => import('./pages/IssuePage'));
const SupportDashboard = lazy(() => import('./pages/SupportDashboard'));
const QRScannerPage = lazy(() => import('./pages/QRScannerPage'));
const SubmitEventPage = lazy(() => import('./pages/SubmitEventPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const BecomeOrganizerPage = lazy(() => import('./pages/BecomeOrganizerPage'));
const WorkWithUsOrganizerPage = lazy(() => import('./pages/WorkWithUsOrganizerPage'));
const OrganizerProfilePage = lazy(() => import('./pages/OrganizerProfilePage'));
const OrganizerDashboard = lazy(() => import('./pages/OrganizerDashboard'));
const OrganizerPricingPage = lazy(() => import('./pages/OrganizerPricingPage'));
const OrganizerOnboardPage = lazy(() => import('./pages/OrganizerOnboardPage'));
const UnifiedDashboard = lazy(() => import('./pages/UnifiedDashboard'));
const VerifyEmailChangePage = lazy(() => import('./pages/VerifyEmailChangePage'));
const AppDownloadPage = lazy(() => import('./pages/AppDownloadPage'));
const StripeConnectRedirect = lazy(() => import('./pages/StripeConnectRedirect'));
const AdvertisePage = lazy(() => import('./pages/AdvertisePage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const FollowingPage = lazy(() => import('./pages/FollowingPage'));
const RefundRequestPage = lazy(() => import('./pages/RefundRequestPage'));
const TicketPassPage = lazy(() => import('./pages/TicketPassPage'));
const OrganizerMoneyPage = lazy(() => import('./pages/OrganizerMoneyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const AcceptableUsePolicyPage = lazy(() => import('./pages/AcceptableUsePolicyPage'));
const AccessibilityStatementPage = lazy(() => import('./pages/AccessibilityStatementPage'));
// Shared with passwordGateRules.js — see constants/cities.js.

// Fetch CSRF token once on app start — stored in memory, used by all non-GET requests
initializeCsrf();

function OnboardingGuard({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return children;

  // Remove: '/account-settings', '/organizer/tickets', '/pricing'
  const exempt = [
    '/onboarding', '/login', '/register', '/verify-email',
    '/auth', '/work-with-us',
    '/forgot-password', '/reset-password', '/verify-email-change',
    '/profile', '/organizer', '/host', '/submit-event',
    '/become-organizer', '/organizer-onboarding', '/organizer-onboard', '/support', '/privacy', '/dashboard',
    '/terms', '/cookies', '/acceptable-use', '/accessibility', '/help', '/contact', '/delete-account', '/get-app', '/download', '/advertise', '/partners', '/promote-event', '/partner-portal', '/following', '/refund-request',
    '/events',
  ].some(p => location.pathname.startsWith(p));

  // Organizers, admins, and support are never blocked by onboarding
  const isOrgOrAdmin = user?.role === 'organizer' || user?.role === 'admin' || user?.role === 'support';

  if (isOrgOrAdmin && location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/dashboard" replace />;
  }

  const hasDoneOnboarding = !!(user?.hasCompletedOnboarding || user?.onboardingCompleted);
  if (user && !hasDoneOnboarding && !exempt && !isOrgOrAdmin) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

// A "please verify your email" banner used to sit here. Signup goes through
// /otp/complete-signup, which only creates the account once the emailed code has
// been entered and so sets isEmailVerified: true — the banner could not appear
// for anyone who signed up through the app, and it pointed at a resend link for
// a verification email that flow never sends. Legacy accounts still holding
// isEmailVerified: false are handled from the admin panel.

const ROUTE_META = [
  { match: path => path === '/', title: 'Vibana - African Events Near You', description: 'Discover the best African cultural events, music, food, and art near you. Browse events in Houston, Atlanta, New York, Toronto and more.' },
  { match: path => path === '/events', title: 'Events - Vibana', description: 'Browse African cultural events happening near you. Filter by city, vibe, and date.' },
  { match: path => path.startsWith('/events/'), title: 'Event Details - Vibana', description: 'View event details, venue information, and tickets for African cultural events on Vibana.' },
  { match: path => path === '/magazine', title: 'Community - Vibana Magazine', description: 'Stories, interviews, and features from the African diaspora. Business spotlights, culture, and community.' },
  { match: path => path === '/tv', title: 'Vibana TV - African Videos & Culture', description: 'Watch the latest videos from the African diaspora. Events, culture, music, and community stories on Vibana TV.' },
  { match: path => path.startsWith('/magazine/issue/'), title: 'Vibana Magazine Issue', description: 'An issue of Vibana Magazine — stories, video and events from the African diaspora, gathered for the month.' },
  { match: path => path.startsWith('/magazine/'), title: 'Vibana Magazine Story', description: 'Read the latest stories, interviews, and features from the African diaspora on Vibana Magazine.' },
  { match: path => path === '/profile', title: 'My Profile - Vibana', description: 'Manage your Vibana profile, tickets, saved events, and account settings.' },
  { match: path => path === '/help', title: 'Help & FAQ - Vibana', description: 'Answers to common questions about Vibana events, tickets, refunds, and organizer accounts.' },
  { match: path => path === '/contact', title: 'Contact Us - Vibana', description: 'Get in touch with the Vibana team for support, partnerships, press, or feedback. We reply within 24 hours Mon-Fri.' },
  { match: path => path === '/work-with-us/organizers', title: 'Work With Us - Organizers | Vibana', description: 'Join Vibana as an organizer and reach the African diaspora through events, community, and culture.' },
  { match: path => path === '/advertise', title: 'Advertise on Vibana', description: 'Partner with Vibana to reach African diaspora audiences through events, magazine features, and sponsorship campaigns.' },
  { match: path => path === '/about', title: 'About Vibana', description: 'Vibana is the platform for African cultural events across North America.' },
  { match: path => path === '/submit-event', title: 'Submit Event - Vibana', description: 'Submit your African cultural event to Vibana and connect with audiences across North America.' },
  { match: path => path === '/auth' || path === '/login' || path === '/register', title: 'Sign In - Vibana', description: 'Sign in or create a Vibana account to save events, buy tickets, and follow organizers.' },
  { match: path => path === '/forgot-password', title: 'Forgot Password - Vibana', description: 'Reset your Vibana password and regain access to your account securely.' },
  { match: path => path === '/reset-password', title: 'Reset Password - Vibana', description: 'Set a new Vibana password to continue managing your events and tickets.' },
  { match: path => path === '/verify-email' || path === '/verify-email-change', title: 'Verify Email - Vibana', description: 'Verify your Vibana email address to secure your account and unlock all features.' },
  { match: path => path === '/privacy', title: 'Privacy Policy - Vibana', description: 'Read Vibana privacy policy and learn how your account and event data are handled.' },
  { match: path => path === '/cookies', title: 'Cookie Policy - Vibana', description: 'Learn how Vibana uses cookies and similar technologies to enhance your experience.' },
  { match: path => path === '/acceptable-use', title: 'Acceptable Use Policy - Vibana', description: 'Review Vibana guidelines for content, events, and responsible community use.' },
  { match: path => path === '/accessibility', title: 'Accessibility Statement - Vibana', description: 'Vibana is committed to accessibility. Learn about our features and accommodations for users with disabilities.' },
  { match: path => path === '/terms', title: 'Terms of Service - Vibana', description: 'Review Vibana terms of service for users, ticket buyers, organizers, and partners.' },
  { match: path => path === '/delete-account', title: 'Delete Account - Vibana', description: 'Learn how to permanently delete your Vibana account and related data.' },
  { match: path => path === '/onboarding', title: 'Onboarding - Vibana', description: 'Set your city and interests to personalize your Vibana event recommendations.' },
  { match: path => path === '/become-organizer', title: 'Become an Organizer - Vibana', description: 'Apply to become a Vibana organizer and start publishing events to your audience.' },
  { match: path => path === '/support' || path.startsWith('/support/'), title: 'Support Dashboard - Vibana', description: 'Access support tools and resources to manage tickets, issues, and customer requests on Vibana.' },
  { match: path => path === '/organizer/pricing', title: 'Organizer Pricing - Vibana', description: 'View Vibana organizer pricing, fees, and platform benefits for event hosts.' },
  { match: path => path === '/organizer/scan', title: 'QR Scanner - Vibana', description: 'Scan attendee tickets quickly with Vibana organizer QR check-in tool.' },
  { match: path => path === '/organizer-onboard', title: 'Organizer Onboarding - Vibana', description: 'Complete your organizer account setup and get early access to the Vibana dashboard.' },
  { match: path => path.startsWith('/organizer/'), title: 'Organizer Dashboard - Vibana', description: 'Manage event listings, ticket sales, and performance from your Vibana organizer dashboard.' },
  { match: path => path.startsWith('/dashboard/'), title: 'Dashboard - Vibana', description: 'Access your Vibana dashboard for account, event, and performance management.' },
  { match: path => path.startsWith('/host/'), title: 'Organizer Profile - Vibana', description: 'Explore organizer profiles, upcoming events, and community activity on Vibana.' },
  { match: path => path === '/get-app' || path === '/download', title: 'Get the Vibana App', description: 'Download the Vibana app to discover events, buy tickets, and follow organizers on the go.' },
  { match: path => path === '/payment-success', title: 'Payment Success - Vibana', description: 'Your Vibana payment is complete. Access your tickets and event details instantly.' },
  { match: path => path === '/refund-request', title: 'Refund Request - Vibana', description: 'Submit a Vibana refund request for eligible tickets and purchases.' },
  { match: path => path === '/following', title: 'Following - Vibana', description: 'View and manage organizers and communities you follow on Vibana.' },
];

function RouteHelmet() {
  const location = useLocation();
  const { pathname } = location;
  const meta = ROUTE_META.find(item => item.match(pathname)) || {
    title: 'Vibana — African Events',
    description: 'Discover African events, culture, and community stories across North America.',
  };
  const url = `https://vibana.app${pathname}`;

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:image" content="https://vibana.app/og-image.jpg" />
    </Helmet>
  );
}

/**
 * Tell GA4 and the Pixel about client-side navigation.
 *
 * A single-page app loads one document and then rewrites it, so without this
 * every session reports exactly one page — whichever URL the visitor happened
 * to arrive on — and the entire browse funnel is invisible. gtag is configured
 * with send_page_view:false for the app (see public/analytics.js), so the first
 * render is this component's job too, not a duplicate of one gtag already sent.
 *
 * Both calls are optional-chained: analytics is only injected on production
 * builds, so on every preview and local run these globals are simply absent.
 */
function PageViews() {
  const location = useLocation();
  const firstPageView = useRef(true);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_path: path, page_location: window.location.href });
    }
    // The Pixel's bootstrap already fired the first PageView, so skip that one
    // here rather than counting the landing hit twice.
    if (firstPageView.current) {
      firstPageView.current = false;
      return;
    }
    if (typeof window.fbq === 'function') window.fbq('track', 'PageView');
  }, [location.pathname, location.search]);

  return null;
}

function EventRouteResolver() {
  const { id } = useParams();
  if (CITY_SLUGS.has((id || '').toLowerCase())) return <CityEventsPage />;
  return <EventDetailPage />;
}

// The support chat is a floating button over whatever page you are on, so it
// is scoped rather than global: it shows where someone has money, a ticket or
// an account at stake, and stays off the pages that are the brand — the home
// page, events, the magazine, Vibana TV, the legal and marketing pages. A
// red circle hovering over a magazine article is an advert for a problem.
// Prefix match, so '/organizer' also covers '/organizer/earnings' and
// '/organizer-onboarding'.
const SUPPORT_CHAT_ROUTES = [
  // Paid for something and not sure it worked.
  '/payment-success', '/my-tickets', '/refund-request',
  // Their own account.
  '/profile', '/delete-account', '/onboarding',
  // Asking for help is the one place it is the point.
  '/help', '/contact', '/support',
  // Organizers, including the pages where they sign up: these are the
  // conversations worth interrupting a layout for.
  '/organizer', '/dashboard', '/become-organizer', '/work-with-us/organizers',
  '/submit-event', '/partner-portal',
];

function ScopedSupportChat() {
  const { pathname } = useLocation();
  if (!SUPPORT_CHAT_ROUTES.some((r) => pathname.startsWith(r))) {
    return null;
  }
  return <SupportChatWidget />;
}

// Re-mounts the boundary on every navigation so a crash on one route never
// poisons the next route. Keeps Navbar / Footer mounted.
function RouteBoundary({ children }) {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname} compact>
      {children}
    </ErrorBoundary>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
      <ToastProvider>
        <ErrorBoundary>
        <Router>
        {/* Outside PasswordGate on purpose: pre-launch, the number of people
            hitting the wall is one of the few real demand signals available. */}
        <PageViews />
        <PasswordGate>
          <RouteHelmet />
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <a
              href="#main-content"
              style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
              onFocus={e => { e.target.style.left = '16px'; e.target.style.width = 'auto'; e.target.style.height = 'auto'; }}
              onBlur={e => { e.target.style.left = '-9999px'; e.target.style.width = '1px'; e.target.style.height = '1px'; }}
            >
              Skip to main content
            </a>
            <Navbar />
            <main id="main-content" style={{ flex: 1 }}>
              <OnboardingGuard>
              <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 40, height: 40, border: '3px solid #E63946', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /></div>}>
              <RouteBoundary>
              <Routes>
  <Route path="/" element={<HomePage />} />
  <Route path="/events" element={<EventsPage />} />
  <Route path="/events/:id" element={<EventRouteResolver />} />
  <Route path="/events/:citySlug" element={<CityEventsPage />} />
  <Route path="/submit-event" element={<SubmitEventPage />} />
  <Route path="/magazine" element={<MagazinePage />} />
  <Route path="/tv" element={<Navigate to="/magazine?tab=videos" replace />} />
  {/* Three segments, so it can never be read as /magazine/:slug — that route
      is an article, and an issue slug sharing a title with one would otherwise
      shadow it. */}
  <Route path="/magazine/issue/:slug" element={<IssuePage />} />
  <Route path="/magazine/:slug" element={<ArticlePage />} />
  <Route path="/login" element={<AuthPage />} />
  <Route path="/register" element={<AuthPage />} />
  <Route path="/auth" element={<AuthPage />} />
  <Route path="/profile" element={<ProfilePage />} />
  <Route path="/admin/*" element={<Navigate to="/" replace />} />
  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
  <Route path="/reset-password" element={<ResetPasswordPage />} />
  <Route path="/verify-email" element={<VerifyEmailPage />} />
  <Route path="/verify-email-change" element={<VerifyEmailChangePage />} />
  <Route path="/privacy" element={<PrivacyPolicyPage />} />
  <Route path="/cookies" element={<CookiePolicyPage />} />
  <Route path="/acceptable-use" element={<AcceptableUsePolicyPage />} />
  <Route path="/accessibility" element={<AccessibilityStatementPage />} />
  <Route path="/onboarding" element={<OnboardingPage />} />
  <Route path="/become-organizer" element={<BecomeOrganizerPage />} />
  <Route path="/work-with-us/organizers" element={<WorkWithUsOrganizerPage />} />
  <Route path="/organizer-onboarding" element={<Navigate to="/work-with-us/organizers" replace />} />
  <Route path="/support/*" element={<SupportDashboard />} />
  <Route path="/organizer/scan" element={<QRScannerPage />} />
  <Route path="/organizer/pricing" element={<OrganizerPricingPage />} />
  <Route path="/organizer-onboard" element={<OrganizerOnboardPage />} />
  <Route path="/organizer/*" element={<OrganizerDashboard />} />
  <Route path="/dashboard/*" element={<UnifiedDashboard />} />
  <Route path="/host/:id" element={<OrganizerProfilePage />} />
  <Route path="/help" element={<HelpPage />} />
  <Route path="/contact" element={<ContactPage />} />
  <Route path="/delete-account" element={<DeleteAccountPage />} />
  <Route path="/notifications" element={<Navigate to="/get-app" replace />} />
  <Route path="/saved-events" element={<Navigate to="/get-app" replace />} />
  <Route path="/get-app" element={<AppDownloadPage />} />
  <Route path="/download" element={<Navigate to="/get-app" replace />} />
  <Route path="/advertise" element={<AdvertisePage />} />
  <Route path="/terms" element={<TermsPage />} />
  <Route path="/organizer/earnings" element={<StripeConnectRedirect />} />
  <Route path="/partners" element={<Navigate to="/advertise" replace />} />
  <Route path="/promote-event" element={<Navigate to="/advertise" replace />} />
  <Route path="/my-tickets" element={<Navigate to="/profile?section=tickets" replace />} />
  <Route path="/payment-success" element={<PaymentSuccessPage />} />
  <Route path="/refund-request" element={<RefundRequestPage />} />
  <Route path="/ticket" element={<TicketPassPage />} />
  <Route path="/organizers/how-you-get-paid" element={<OrganizerMoneyPage />} />
  <Route path="/partner-portal" element={<Navigate to="/advertise" replace />} />
  <Route path="/leaderboard" element={<Navigate to="/" replace />} />
  <Route path="/following" element={<FollowingPage />} />
  <Route path="/proposal/:token" element={<ProposalPage />} />
  <Route path="*" element={<NotFoundPage />} />
</Routes>
              </RouteBoundary>
              </Suspense>
              </OnboardingGuard>
            </main>
            <Footer />
            <ScopedSupportChat />
          </div>
          <ToastContainer />
        </PasswordGate>
        </Router>
        </ErrorBoundary>
      </ToastProvider>
      </AuthProvider>
      </ThemeProvider>
  );
}

// -- 404 PAGE --------------------------------------------------
function NotFoundPage() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 72, fontWeight: 900, color: 'var(--red)', fontFamily: 'var(--font-display)', lineHeight: 1 }}>404</div>
      <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--void)', margin: '16px 0 10px', fontFamily: 'var(--font-display)' }}>Page not found</h2>
      <p style={{ color: '#6B7280', fontSize: 16, marginBottom: 28 }}>The page you are looking for does not exist or has been moved.</p>
      <Link to="/" style={{ padding: '13px 28px', borderRadius: 10, background: 'var(--red)', color: '#fff', fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>
        Back to Home
      </Link>
    </div>
  );
}

export default App;
